import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { fetchClientsForAdvisorByAdvisorIdRequest, selectMaybeClient, selectSessionAdvisor } from '../model';
import { builtEnvironment } from '@shared/builtEnvironment';
import { isRunningLocal } from '@shared/isRunningEnvironment';
import { isNullOrUndefined } from '@shared/isNullOrUndefined';

interface UseNavigateToIntakeFormResult {
  navigateToIntakeForm: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

interface UseNavigateToIntakeFormOptions {
  clientId?: string;
}

export function useNavigateToIntakeForm(
  options?: UseNavigateToIntakeFormOptions
): UseNavigateToIntakeFormResult {
  const { getAccessTokenSilently } = useAuth0();
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const client = useSelector(selectMaybeClient);
  const advisor = useSelector(selectSessionAdvisor);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigateToIntakeForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const currentClientId = options?.clientId ?? routeClientId ?? client?.clientId;

      if (!currentClientId) {
        const errorMessage = 'Client ID not found. Please try again.';
        setError(errorMessage);
        toast.error('Failed to load intake form', {
          description: errorMessage,
        });
        return;
      }

      const apiBase = `/${isRunningLocal() ? 'dev' : builtEnvironment}/api`;

      await fetch(`${apiBase}/reopen-form/${currentClientId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(
        `${apiBase}/client/${currentClientId}/form-reference`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData?.errorMessage ?? 'Failed to load intake form. Please try again.';
        setError(errorMessage);
        toast.error('Failed to load intake form', {
          description: errorMessage,
        });
        return;
      }

      const { formReferenceId } = await response.json();
      const baseUrl = window.location.origin;
      const formUrl = `${baseUrl}/onboarding-form/${formReferenceId}`;
      window.open(formUrl, '_blank', 'noopener,noreferrer');

      const handleFocus = () => {
        if (!isNullOrUndefined(advisor?.advisorId)) {
          dispatch(
            fetchClientsForAdvisorByAdvisorIdRequest({
              advisorId: advisor!.advisorId,
              isInvalidation: true,
            }),
          );
        }
        window.removeEventListener('focus', handleFocus);
      };
      window.addEventListener('focus', handleFocus);
    } catch (err) {
      const errorMessage =
        'An error occurred while loading the intake form. Please try again.';
      setError(errorMessage);
      toast.error('Failed to load intake form', {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, options?.clientId, routeClientId, client?.clientId, advisor, dispatch]);

  return {
    navigateToIntakeForm,
    isLoading,
    error,
  };
}

